import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { diff as DiffEditor } from 'react-ace';
import 'ace-builds/src-min-noconflict/theme-github';
import { toast } from 'react-toastify';
import DiffMatchPatch from 'diff-match-patch';

import { Button } from 'components/Button/RoundButton';
import { Wrapper } from 'components/Layout/Layout';
import services from 'api/services';
import './CodeComparisonPage.css';
import Dropdown from 'components/CustomizableDropdown/CustomizableDropdown';
import { AnchorLink } from '../TicketsPage/styles';
import { CodeWrittenText } from '../ReportPage/components/styles';
import { Row, InfoWrapper, LabelHeading, LabelValue } from './styles';
import { Checkbox } from 'components/Checkbox/Checkbox';

const { TicketService } = services;

export default function CodeComparisonPage() {
  const { ticketId, anonymised } = useParams();
  const [oldInput, setOldInput] = useState({});
  const [newInput, setNewInput] = useState({});
  const [ticket, setTicket] = useState({});
  const [testName, setTestName] = useState('');
  const [oldCode, setOldCode] = useState('');
  const [newCode, setNewCode] = useState('');
  const [allInputs, setAllInputs] = useState([]);
  const [showWhitespaceDifference, setShowWhitespaceDifference] = useState(false);

  useEffect(() => {
    const getInputs = async () => {
      try {
        let report;
        if (anonymised === 'true') {
          report = await TicketService.loadAnonymiseReport(ticketId);
        } else {
          report = await TicketService.loadReport(ticketId);
        }
        setAllInputs(report.answers);
        setTicket(report.ticket);
        setTestName(report.test.name);
        if (report.answers.length > 0) setOldInput(report.answers[0]);
        if (report.answers.length > 1) setNewInput(report.answers[1]);
      } catch (error) {
        toast.error(error?.message || 'Error fetching inputs');
      }
    };
    getInputs();
  }, [anonymised, ticketId]);

  const getCodeDifference = useCallback(
    async (input1, input2) => {
      let diff = new DiffMatchPatch();
      diff.Diff_EditCost = 8;
      let textDiff = diff.diff_main(input1, input2);
      diff.diff_cleanupEfficiency(textDiff);
      let oldCode = '';
      let newCode = '';
      for (let i = 0, j = textDiff.length; i < j; i++) {
        let arr = textDiff[i];
        if (showWhitespaceDifference || arr[1].trim() !== '') {
          if (arr[0] === 0) {
            oldCode += arr[1];
            newCode += arr[1];
          } else if (arr[0] === -1) {
            oldCode += arr[1];
          } else {
            newCode += arr[1];
          }
        }
      }

      return { oldCode, newCode };
    },
    [showWhitespaceDifference]
  );

  useEffect(() => {
    const getDiff = async () => {
      try {
        if (oldInput?.code === undefined || newInput?.code === undefined) return;
        const response = await getCodeDifference(oldInput.code, newInput.code);
        setOldCode(response.oldCode);
        setNewCode(response.newCode);
      } catch (error) {
        toast.error(error?.message || 'Error fetching inputs');
      }
    };
    getDiff();
  }, [oldInput, newInput, getCodeDifference]);

  const msToMinutesAndSeconds = (milliseconds) => {
    let minutes = Math.floor(milliseconds / 60000);
    let seconds = ((milliseconds % 60000) / 1000).toFixed(0);
    return seconds === 60 ? minutes + 1 + ':00' : minutes + ':' + (seconds < 10 ? '0' : '') + seconds;
  };

  const getTimeOfRun = (input) => {
    let runTime = new Date(input.timeOfRequest || input.timeOfPaste || input.updatedAt);
    let startTime = new Date(ticket.startedAt);
    return msToMinutesAndSeconds(runTime - startTime);
  };

  const getOptionsForDropdown = () => {
    return allInputs.map((i) => {
      return {
        value: i._id,
        label: ` ${getTimeOfRun(i)} - ${i.levelScores.reduce((acc, curr) => {
          return acc + (curr ? curr : 0);
        }, 0)}/${i.levelMaxScores.reduce((acc, curr) => {
          return acc + (curr ? curr : 0);
        }, 0)} ${i.isBestRun ? ' - Best Run' : i.afterDeadline ? ' - Overtime' : i.isFinalSubmit ? ' - Submit' : ''}`,
      };
    });
  };

  const height = useMemo(() => {
    const fontSize = 12;
    const values = [oldCode, newCode];
    const aceLine = document.getElementsByClassName('ace_line')?.[1];
    const lineHeight = aceLine?.clientHeight || fontSize + 5;
    const max = Math.max(...values.map((e) => e.split(/\r\n|\r|\n/).length));
    const newHeight = max * lineHeight + 5;
    return newHeight + 50;
  }, [newCode, oldCode]);

  return (
    <Wrapper padding="80px 0 30px 0" minHeight="100vh">
      <Row>
        <AnchorLink href={window.location.protocol + '//' + window.location.host + '/report/' + ticket._id + '/'}>
          <Button small primaryBlue primaryGreenBorder usePadding="2px">
            Back to report
          </Button>
        </AnchorLink>
        <InfoWrapper>
          <LabelHeading>Test Name</LabelHeading>: <LabelValue>{testName}</LabelValue>
        </InfoWrapper>
        {anonymised === 'false' && (
          <InfoWrapper>
            <LabelHeading>Candidate Name</LabelHeading> : <LabelValue>{ticket.name}</LabelValue>
          </InfoWrapper>
        )}
        <Checkbox
          checked={showWhitespaceDifference}
          label="Show whitespace difference"
          onChange={() => setShowWhitespaceDifference(!showWhitespaceDifference)}
        />
      </Row>

      <Row>
        <Dropdown
          width="240px"
          options={getOptionsForDropdown()}
          value={oldInput._id}
          onChange={(selected) => {
            setOldInput(allInputs.find((i) => i._id === selected.value));
          }}
        />
        <Dropdown
          width="240px"
          options={getOptionsForDropdown()}
          value={newInput._id}
          onChange={(selected) => {
            setNewInput(allInputs.find((i) => i._id === selected.value));
          }}
        />
      </Row>
      <Row>
        <CodeWrittenText>Code Written in {oldInput.languageVersion} </CodeWrittenText>
        <CodeWrittenText>Code Written in {newInput.languageVersion} </CodeWrittenText>
      </Row>

      <DiffEditor
        wrapEnabled={false}
        value={[oldCode, newCode]}
        height={`${height}px`}
        width="70vw"
        mode="text"
        readOnly
        showPrintMargin={false}
        maxLines={Infinity}
        name="diff_editor"
        highlightActiveLine={false}
      />
    </Wrapper>
  );
}
